import React from 'react';
import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Sidebar } from '@/components/ui-v2/sidebar';
import { StreamingCount } from '@/components/ui-v2/streaming-count';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import { useUser } from '@/hooks/use-user';
import {
  ActorRole,
  DealItem,
  ValidationRuleId,
} from '@/lib/graphql/generated/graphql';
import {
  cn,
  getVatLabelKey,
  hasCriticalErrors,
  isProductModified,
} from '@/lib/utils';
import { filterBrandVisibleValidationErrors } from '@/lib/utils/brand-validation-visibility';
import { getAllFieldValues } from '@/lib/utils/filter-utils';
import { categoryTreeMapState } from '@/states/category-tree-map.state';
import {
  ColumnFilterId,
  columnFiltersState,
} from '@/states/column-filters.state';
import { displayModeState } from '@/states/display-mode.state';
import { filterSidebarAccordionDefaultValueState } from '@/states/filter-sidebar-accordion.state';
import { filteredRowsCountState } from '@/states/filtered-flat-rows.state';
import { NO_CATEGORY_FILTER_KEY } from '@/states/product-category-filter.state';
import { productsState } from '@/states/products.state';
import { selectedRowsCountState } from '@/states/selected-rows-count.state';
import { storeAtom } from '@/states/store.state';
import { ExtendedValidationError } from '@/types/validation-error';
import { useAtom, useAtomValue } from 'jotai';
import { AlertCircle, AlertTriangle, Check } from 'lucide-react';

import { editionModeState } from '../states/edition-mode.state';
import FilterSidebarRangeFilter from './filter-sidebar-range-filter';

export interface FilterSidebarProps {
  onClose: () => void;
}

const FilterSidebar: React.FC<FilterSidebarProps> = ({ onClose }) => {
  const { t } = useTranslation();
  const store = useAtomValue(storeAtom);
  const vatLabel = t(getVatLabelKey(store));
  const selectedRowsCount = useAtomValue(selectedRowsCountState);
  const filteredRowsCount = useAtomValue(filteredRowsCountState);
  const [columnFilters, setColumnFilters] = useAtom(columnFiltersState);
  const accordionDefaultValue = useAtomValue(
    filterSidebarAccordionDefaultValueState
  );

  const products = useAtomValue(productsState);

  // Get productCategory filter from columnFilters
  const productCategoryFilter = useMemo(() => {
    const filter = columnFilters.find(
      (f) => f.id === ColumnFilterId.productCategory
    );
    return (filter?.value as string[]) || [];
  }, [columnFilters]);

  // Helper to update productCategory filter in columnFilters
  const setProductCategoryFilter = useCallback(
    (categories: string[]) => {
      setColumnFilters((previous) => {
        const existing = previous.find(
          (f) => f.id === ColumnFilterId.productCategory
        );
        if (existing) {
          return previous.map((f) =>
            f.id === ColumnFilterId.productCategory
              ? { ...f, value: categories }
              : f
          );
        }
        return [
          ...previous,
          { id: ColumnFilterId.productCategory, value: categories },
        ];
      });
    },
    [setColumnFilters]
  );
  const user = useUser();

  const productErrorsFilter = useMemo(() => {
    const filter = columnFilters.find(
      (f) => f.id === ColumnFilterId.validationErrors
    );
    return (filter?.value as string[]) || [];
  }, [columnFilters]);

  const setProductErrorsFilter = useCallback(
    (ruleIds: string[]) => {
      setColumnFilters((previous) => {
        const existing = previous.find(
          (f) => f.id === ColumnFilterId.validationErrors
        );
        if (existing) {
          return previous.map((f) =>
            f.id === ColumnFilterId.validationErrors
              ? { ...f, value: ruleIds }
              : f
          );
        }
        return [
          ...previous,
          { id: ColumnFilterId.validationErrors, value: ruleIds },
        ];
      });
    },
    [setColumnFilters]
  );

  const displayMode = useAtomValue(displayModeState);
  const editionMode = useAtomValue(editionModeState);
  const isInternalUser = !!user?.isInternalUser;

  const categoryTreeMap = useAtomValue(categoryTreeMapState);

  const filteredProducts = useMemo(() => {
    const filtered = products.filter((p) => {
      switch (displayMode) {
        case 'selected':
          return p.activeVersion.isSelected;
        case 'deselected':
          return !p.activeVersion.isSelected;
        case 'error':
          return (
            p.activeVersion.isSelected &&
            hasCriticalErrors(
              filterBrandVisibleValidationErrors(
                p.activeVersion.validationErrors ?? [],
                isInternalUser
              )
            )
          );
        case 'modified':
          return user?.role
            ? isProductModified(p, user.role, editionMode)
            : false;
        case 'modified_by_choose':
          return isProductModified(p, ActorRole.Brand, editionMode);
      }
    });

    return filtered.map((product) => {
      const productCategoryId =
        product.activeVersion.productCategory?.productCategoryId;
      return {
        ...product,
        localeLabel: productCategoryId
          ? categoryTreeMap[productCategoryId]
          : undefined,
      };
    });
  }, [
    products,
    displayMode,
    user,
    isInternalUser,
    editionMode,
    categoryTreeMap,
  ]);

  const filterRange = useCallback(
    (key: keyof DealItem) => {
      // Use shared utility to get all values (handles variants correctly)
      const values = getAllFieldValues(filteredProducts, key);

      // Field-specific default ranges when no values found
      if (values.length === 0) {
        const defaultRanges: Record<
          keyof DealItem,
          { min: number; max: number }
        > = {
          listPrice: { min: 0, max: 1000 },
          discountedPrice: { min: 0, max: 1000 },
          choosePrice: { min: 0, max: 1000 },
          chooseDiscount: { min: 0, max: 100 },
          stockQuantity: { min: 0, max: 99999 },
        } as Record<keyof DealItem, { min: number; max: number }>;
        return defaultRanges[key] || { min: 0, max: 1000 };
      }

      const min = Math.floor(Math.min(...values));
      const max = Math.ceil(Math.max(...values));

      // Ensure min <= max (edge case protection)
      return { min: Math.min(min, max), max: Math.max(min, max) };
    },
    [filteredProducts]
  );

  const listPriceRange = useMemo(() => filterRange('listPrice'), [filterRange]);
  const discountedPriceRange = useMemo(
    () => filterRange('discountedPrice'),
    [filterRange]
  );
  const choosePriceRange = useMemo(
    () => filterRange('choosePrice'),
    [filterRange]
  );
  const chooseDiscountRange = useMemo(
    () => filterRange('chooseDiscount'),
    [filterRange]
  );
  const stockQuantityRange = useMemo(
    () => filterRange('stockQuantity'),
    [filterRange]
  );

  // Category filter calculations - use IDs for filtering, labels for display
  const productsWithoutCategoryCount = useMemo(() => {
    return filteredProducts.filter(
      (product) => !product.activeVersion.productCategory?.productCategoryId
    ).length;
  }, [filteredProducts]);

  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    filteredProducts.forEach((product) => {
      const categoryId =
        product.activeVersion.productCategory?.productCategoryId;
      if (categoryId) {
        counts[categoryId] = (counts[categoryId] || 0) + 1;
      }
    });
    return counts;
  }, [filteredProducts]);

  const errorCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    filteredProducts.forEach((product) => {
      filterBrandVisibleValidationErrors(
        product.activeVersion.validationErrors ?? [],
        isInternalUser
      ).forEach((error) => {
        counts[error.ruleId] = (counts[error.ruleId] || 0) + 1;
      });
    });
    return counts;
  }, [filteredProducts, isInternalUser]);

  // Map of category ID to locale label for display
  const categoryIdToLabel = useMemo(() => {
    const map: Record<string, string> = {};
    filteredProducts.forEach((product) => {
      const categoryId =
        product.activeVersion.productCategory?.productCategoryId;
      if (categoryId && product.localeLabel) {
        map[categoryId] = product.localeLabel;
      }
    });
    return map;
  }, [filteredProducts]);

  const uniqueProductCategories = useMemo(() => {
    const categoryIds = new Set<string>();
    filteredProducts.forEach((product) => {
      const categoryId =
        product.activeVersion.productCategory?.productCategoryId;
      if (categoryId) {
        categoryIds.add(categoryId);
      }
    });
    return Array.from(categoryIds).sort((a, b) => {
      const aLabel = categoryIdToLabel[a] || '';
      const bLabel = categoryIdToLabel[b] || '';
      const aLastSegment = aLabel.split(' > ').reverse()[0];
      const bLastSegment = bLabel.split(' > ').reverse()[0];
      return aLastSegment.localeCompare(bLastSegment);
    });
  }, [filteredProducts, categoryIdToLabel]);

  const uniqueProductErrors = useMemo(() => {
    const errors = new Map<
      ValidationRuleId,
      ExtendedValidationError['severity']
    >();
    filteredProducts.forEach((product) => {
      filterBrandVisibleValidationErrors(
        product.activeVersion.validationErrors ?? [],
        isInternalUser
      ).forEach((error) => {
        errors.set(error.ruleId, error.severity);
      });
    });
    return {
      errors,
    };
  }, [filteredProducts, isInternalUser]);
  // Auto-reset invalid filters when view changes (filters outside current range)
  useEffect(() => {
    const filtersToReset: string[] = [];
    // oxlint-disable-next-line no-unused-vars
    let validationErrorsValueToTrim: string[] | null = null;

    columnFilters.forEach((filter) => {
      if (filter.id === ColumnFilterId.validationErrors) {
        const value = (filter.value as string[]) || [];
        const validValue = value.filter((id) =>
          uniqueProductErrors.errors.has(id as ValidationRuleId)
        );
        if (validValue.length !== value.length) {
          validationErrorsValueToTrim = validValue;
        }
        return;
      }

      if (filter.id === 'productCategory') {
        for (const id of filter.value as Array<string>) {
          if (id === NO_CATEGORY_FILTER_KEY) continue;
          if (!uniqueProductCategories.includes(id)) {
            filtersToReset.push(filter.id);
            break;
          }
        }
        return;
      }

      // range filter as fallback
      if (Array.isArray(filter.value) && filter.value.length === 2) {
        const filterMin = Number(filter.value[0]);
        const filterMax = Number(filter.value[1]);

        if (isNaN(filterMin) || isNaN(filterMax)) {
          filtersToReset.push(filter.id);
          return;
        }

        // Get the valid range for this field in the current view
        const range = filterRange(filter.id as keyof DealItem);

        // Check if filter is outside the valid range
        // If filter min > range max OR filter max < range min, the filter is invalid
        if (filterMin > range.max || filterMax < range.min) {
          filtersToReset.push(filter.id);
        }
        return;
      }
    });

    // Reset invalid filters by removing them (they'll be recreated with default values if needed)
    if (filtersToReset.length > 0) {
      setColumnFilters((previous) =>
        previous.filter((f) => !filtersToReset.includes(f.id))
      );
    }

    // Trim validation errors filter to only valid IDs in current view
    if (validationErrorsValueToTrim !== null) {
      setColumnFilters((previous) =>
        previous.map((f) =>
          f.id === ColumnFilterId.validationErrors
            ? { ...f, value: validationErrorsValueToTrim }
            : f
        )
      );
    }
  }, [
    filteredProducts,
    columnFilters,
    setColumnFilters,
    filterRange,
    uniqueProductCategories,
    uniqueProductErrors,
  ]);

  // Helper function to render a range filter with automatic initialization
  const renderRangeFilter = useCallback(
    (
      filterId: string,
      range: { min: number; max: number },
      displayUnit?: string
    ) => {
      let filter = columnFilters.find((f) => f.id === filterId);

      // If filter doesn't exist, create it with default range
      if (!filter || !Array.isArray(filter.value)) {
        const defaultFilter = {
          id: filterId,
          value: [range.min, range.max] as number[],
        };
        // Initialize the filter in state
        setColumnFilters((previous) => {
          if (!previous.find((f) => f.id === filterId)) {
            return [...previous, defaultFilter];
          }
          return previous;
        });
        filter = defaultFilter;
      }

      return (
        <FilterSidebarRangeFilter
          filter={filter}
          setFilter={(values: number[]) =>
            setColumnFilters((previous) => [
              ...previous.map((f) => {
                if (f.id !== filterId) return f;
                return {
                  ...f,
                  value: values,
                };
              }),
            ])
          }
          onRemoveFilter={() =>
            setColumnFilters((previous) =>
              previous.filter((f) => f.id !== filterId)
            )
          }
          min={range.min}
          max={range.max}
          labelMinValue={t('components.filter_sidebar.min')}
          labelMaxValue={t('components.filter_sidebar.max')}
          placeholderMinValue={t('components.filter_sidebar.min_placeholder')}
          placeholderMaxValue={t('components.filter_sidebar.max_placeholder')}
          store={store}
          displayUnit={displayUnit}
        />
      );
    },
    [columnFilters, setColumnFilters, t, store]
  );

  return (
    <Sidebar
      title={t('components.filter_sidebar.title')}
      onClose={onClose}
      headerExtra={
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-gray-700">
            {t('components.filter_sidebar.variants_filtered')}
          </span>
          <span
            className={`rounded-full px-3 py-1 text-xs font-semibold ${
              filteredRowsCount === 0 && filteredRowsCount !== selectedRowsCount
                ? 'bg-red-100 text-red-800'
                : 'bg-blue-100 text-blue-800'
            }`}
          >
            {filteredRowsCount} /{' '}
            <StreamingCount
              count={selectedRowsCount}
              className="inline align-text-bottom"
            />
          </span>
        </div>
      }
      contentClassName="space-y-2"
    >
      <Accordion
        type="multiple"
        defaultValue={accordionDefaultValue}
        className="space-y-2"
      >
        <AccordionItem
          value="categories"
          className="rounded-lg border border-gray-200 px-4"
        >
          <AccordionTrigger className="py-3 hover:no-underline">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-gray-900">
                {t('components.other.categories')}
              </span>
              {productCategoryFilter.length > 0 && (
                <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800">
                  {productCategoryFilter.length}
                </span>
              )}
            </div>
          </AccordionTrigger>
          <AccordionContent className="pb-4">
            <div className="space-y-2">
              {productCategoryFilter.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setProductCategoryFilter([])}
                  className="w-full justify-start text-xs text-gray-500"
                >
                  {t('components.product_view.clear_filters')}
                </Button>
              )}
              <div className="max-h-64 space-y-1 overflow-y-auto">
                {productsWithoutCategoryCount > 0 && (
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setColumnFilters((previous) => {
                        const existing = previous.find(
                          (f) => f.id === ColumnFilterId.productCategory
                        );
                        const currentCategories =
                          (existing?.value as string[]) || [];
                        const newCategories = currentCategories.includes(
                          NO_CATEGORY_FILTER_KEY
                        )
                          ? currentCategories.filter(
                              (id) => id !== NO_CATEGORY_FILTER_KEY
                            )
                          : [...currentCategories, NO_CATEGORY_FILTER_KEY];
                        if (existing) {
                          return previous.map((f) =>
                            f.id === ColumnFilterId.productCategory
                              ? { ...f, value: newCategories }
                              : f
                          );
                        }
                        return [
                          ...previous,
                          {
                            id: ColumnFilterId.productCategory,
                            value: newCategories,
                          },
                        ];
                      });
                    }}
                    className={`flex w-full items-center rounded-md px-2 py-1.5 text-left text-sm transition-colors ${
                      productCategoryFilter.includes(NO_CATEGORY_FILTER_KEY)
                        ? 'bg-gray-100 font-medium'
                        : 'hover:bg-gray-50'
                    }`}
                  >
                    <div
                      className={`mr-2 flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border ${
                        productCategoryFilter.includes(NO_CATEGORY_FILTER_KEY)
                          ? 'border-blue-500 bg-blue-500'
                          : 'border-gray-300'
                      }`}
                    >
                      {productCategoryFilter.includes(
                        NO_CATEGORY_FILTER_KEY
                      ) && <Check className="h-3 w-3 text-white" />}
                    </div>
                    <span>
                      {t('components.product_view.no_category')} (
                      <StreamingCount
                        count={productsWithoutCategoryCount}
                        className="inline align-text-bottom"
                      />
                      )
                    </span>
                  </button>
                )}

                {uniqueProductCategories.map((categoryId) => {
                  const categoryLabel = categoryIdToLabel[categoryId] || '';
                  const displayLabel = categoryLabel.includes(' > ')
                    ? categoryLabel.split(' > ').reverse()[0]
                    : categoryLabel;
                  return (
                    <button
                      key={categoryId}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setColumnFilters((previous) => {
                          const existing = previous.find(
                            (f) => f.id === ColumnFilterId.productCategory
                          );
                          const currentCategories =
                            (existing?.value as string[]) || [];
                          const newCategories = currentCategories.includes(
                            categoryId
                          )
                            ? currentCategories.filter(
                                (id) => id !== categoryId
                              )
                            : [...currentCategories, categoryId];
                          if (existing) {
                            return previous.map((f) =>
                              f.id === ColumnFilterId.productCategory
                                ? { ...f, value: newCategories }
                                : f
                            );
                          }
                          return [
                            ...previous,
                            {
                              id: ColumnFilterId.productCategory,
                              value: newCategories,
                            },
                          ];
                        });
                      }}
                      className={`flex w-full items-center rounded-md px-2 py-1.5 text-left text-sm transition-colors ${
                        productCategoryFilter.includes(categoryId)
                          ? 'bg-gray-100 font-medium'
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      <div
                        className={`mr-2 flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border ${
                          productCategoryFilter.includes(categoryId)
                            ? 'border-blue-500 bg-blue-500'
                            : 'border-gray-300'
                        }`}
                      >
                        {productCategoryFilter.includes(categoryId) && (
                          <Check className="h-3 w-3 text-white" />
                        )}
                      </div>
                      <span>
                        {displayLabel} ({categoryCounts[categoryId] || 0})
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Errors */}

        <AccordionItem
          value="errors"
          className="rounded-lg border border-gray-200 px-4"
        >
          <AccordionTrigger className="py-3 hover:no-underline">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-gray-900">
                {t('components.other.errors')}
              </span>
              {productErrorsFilter.length > 0 && (
                <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800">
                  {productErrorsFilter.length}
                </span>
              )}
            </div>
          </AccordionTrigger>
          <AccordionContent className="pb-4">
            <div className="space-y-2">
              {productErrorsFilter.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setProductErrorsFilter([])}
                  className="w-full justify-start text-xs text-gray-500"
                >
                  {t('components.product_view.clear_filters')}
                </Button>
              )}
              <div className="max-h-64 space-y-3 overflow-y-auto">
                {([true, false] as const).map((isErrorGroup) => {
                  const groupIds = [...uniqueProductErrors.errors.keys()]
                    .filter(
                      (id) =>
                        ((uniqueProductErrors.errors.get(id) ?? 'warning') ===
                          'error') ===
                        isErrorGroup
                    )
                    .sort((a, b) =>
                      t(`validation_errors.${a}.ruleName`).localeCompare(
                        t(`validation_errors.${b}.ruleName`)
                      )
                    );
                  if (groupIds.length === 0) return null;
                  return (
                    <div
                      key={isErrorGroup ? 'error' : 'warning'}
                      className="space-y-1"
                    >
                      <div
                        className={cn(
                          'flex items-center gap-1.5 px-2 text-xs font-semibold',
                          isErrorGroup ? 'text-red-500' : 'text-orange-500'
                        )}
                      >
                        {isErrorGroup ? (
                          <AlertCircle className="size-3.5" />
                        ) : (
                          <AlertTriangle className="size-3.5" />
                        )}
                        {t(
                          isErrorGroup
                            ? 'components.alert_sidebar.popover_to_fix'
                            : 'components.alert_sidebar.popover_to_check'
                        )}
                      </div>
                      {groupIds.map((errorId) => {
                        const displayLabel = t(
                          `validation_errors.${errorId}.ruleName`
                        );
                        return (
                          <button
                            key={errorId}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setColumnFilters((previous) => {
                                const existing = previous.find(
                                  (f) =>
                                    f.id === ColumnFilterId.validationErrors
                                );
                                const currentErrors =
                                  (existing?.value as string[]) || [];
                                const newErrors = currentErrors.includes(
                                  errorId
                                )
                                  ? currentErrors.filter((id) => id !== errorId)
                                  : [...currentErrors, errorId];
                                if (existing) {
                                  return previous.map((f) =>
                                    f.id === ColumnFilterId.validationErrors
                                      ? { ...f, value: newErrors }
                                      : f
                                  );
                                }
                                return [
                                  ...previous,
                                  {
                                    id: ColumnFilterId.validationErrors,
                                    value: newErrors,
                                  },
                                ];
                              });
                            }}
                            className={`flex w-full items-center rounded-md px-2 py-1.5 text-left text-sm transition-colors ${
                              productErrorsFilter.includes(errorId)
                                ? 'bg-gray-100 font-medium'
                                : 'hover:bg-gray-50'
                            }`}
                          >
                            <div
                              className={`mr-2 flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border ${
                                productErrorsFilter.includes(errorId)
                                  ? 'border-blue-500 bg-blue-500'
                                  : 'border-gray-300'
                              }`}
                            >
                              {productErrorsFilter.includes(errorId) && (
                                <Check className="h-3 w-3 text-white" />
                              )}
                            </div>
                            <span>
                              {displayLabel} ({errorCounts[errorId] || 0})
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem
          value="listPrice"
          className="rounded-lg border border-gray-200 px-4"
        >
          <AccordionTrigger className="py-3 hover:no-underline">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-gray-900">
                {t('kpi.list_price')} ({vatLabel})
              </span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="pb-4">
            {renderRangeFilter('listPrice', listPriceRange)}
          </AccordionContent>
        </AccordionItem>

        <AccordionItem
          value="choosePrice"
          className="rounded-lg border border-gray-200 px-4"
        >
          <AccordionTrigger className="py-3 hover:no-underline">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-gray-900">
                {t('kpi.choose_price')} ({vatLabel})
              </span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="pb-4">
            {renderRangeFilter('choosePrice', choosePriceRange)}
          </AccordionContent>
        </AccordionItem>

        <AccordionItem
          value="discountedPrice"
          className="rounded-lg border border-gray-200 px-4"
        >
          <AccordionTrigger className="py-3 hover:no-underline">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-gray-900">
                {t('kpi.discounted_price')} ({vatLabel})
              </span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="pb-4">
            {renderRangeFilter('discountedPrice', discountedPriceRange)}
          </AccordionContent>
        </AccordionItem>

        <AccordionItem
          value="chooseDiscount"
          className="rounded-lg border border-gray-200 px-4"
        >
          <AccordionTrigger className="py-3 hover:no-underline">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-gray-900">
                {t('kpi.choose_discount')}
              </span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="pb-4">
            {renderRangeFilter('chooseDiscount', chooseDiscountRange, '%')}
          </AccordionContent>
        </AccordionItem>

        <AccordionItem
          value="stockQuantity"
          className="rounded-lg border border-gray-200 px-4"
        >
          <AccordionTrigger className="py-3 hover:no-underline">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-gray-900">
                {t('kpi.stock')}
              </span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="pb-4">
            {renderRangeFilter('stockQuantity', stockQuantityRange)}
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </Sidebar>
  );
};

export default FilterSidebar;
